/**
 * cashBalance — o SALDO do hero da home: quanto dinheiro já entrou de verdade.
 *
 * ─── POR QUE ISTO NÃO É `aggregateFinanceSummary().balance` ─────────────────
 *
 * O `balance` do resumo do Financeiro é `totalIncome - totalExpense` sobre
 * TODOS os lançamentos do recorte — inclusive os que ainda **não** foram pagos.
 * Ele responde "como o mês deve fechar", que é uma previsão. O hero da home
 * pergunta outra coisa: **quanto o prestador já conseguiu**. Um lançamento com
 * vencimento amanhã não é dinheiro que ele tem.
 *
 * Por isso aqui a régua é uma só: `status === 'paid'`. É o mesmo predicado que
 * o `aggregateFinanceSummary` usa internamente para decidir o que é quitado
 * (`const paid = e.status === 'paid'`) — não `computed_status`, que é a coluna
 * derivada da VIEW e existe para detectar ATRASO, não quitação.
 *
 * ─── AS TRÊS REGRAS QUE QUEBRAM EM SILÊNCIO ────────────────────────────────
 *
 * 1. **Saldo é SALDO, não fluxo.** Não há recorte de período, pelo mesmo motivo
 *    que tirou o seletor de período da home (CLAUDE.md #182): dinheiro que
 *    entrou em março continua sendo dinheiro que ele tem hoje. Comparação entre
 *    meses é assunto de /financeiro e /relatorios.
 *
 * 2. **`null` (não posso ler) ≠ `[]` (posso ler e não há nada).** A RLS de
 *    `finance_entries` é owner/admin-only: para um `member` a consulta devolve
 *    zero linhas, e mostrar "R$ 0,00" seria afirmar que ele não faturou nada —
 *    a mentira dupla do #38. Quem chama passa `null` nesse caso e a tela
 *    ESCONDE o número, em vez de inventar um. Mesma disciplina do
 *    `ClientSummaryMetrics` (regra 5 de `clientSummary.ts`).
 *
 * 3. **`Number(null)` é 0, e aqui isso é certo — mas só porque é explícito.**
 *    `amount` é nullable; lançamento sem valor não soma. Nunca somar `e.amount`
 *    cru (#61: ausência não é zero por acidente, é zero por decisão).
 */
import type { FinanceEntryWithStatus } from '@/types/database';

export interface CashBalance {
  /** Receitas já recebidas. */
  received: number;
  /** Despesas já pagas. */
  paid: number;
  /** `received - paid`. Pode ser negativo — e a tela mostra assim mesmo. */
  balance: number;
  /** Quantos lançamentos quitados entraram na conta (0 = nada quitado ainda). */
  settledCount: number;
}

const EMPTY: CashBalance = { received: 0, paid: 0, balance: 0, settledCount: 0 };

function money(value: unknown): number {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
}

/**
 * O caixa realizado: recebido − pago, sobre os lançamentos QUITADOS.
 *
 * @param entries `null` quando o papel do usuário não pode ler o financeiro —
 *   devolve `null`, e a tela decide o que dizer. Ver a regra 2 no cabeçalho.
 */
export function computeCashBalance(
  entries: readonly FinanceEntryWithStatus[] | null | undefined,
): CashBalance | null {
  if (!entries) return null;

  let received = 0;
  let paid = 0;
  let settledCount = 0;

  for (const entry of entries) {
    // Quitado é `status === 'paid'`, nunca `computed_status` (ver cabeçalho).
    if (entry?.status !== 'paid') continue;
    settledCount += 1;
    if (entry.kind === 'income') received += money(entry.amount);
    else paid += money(entry.amount);
  }

  return { received, paid, balance: received - paid, settledCount };
}

export const EMPTY_CASH_BALANCE: CashBalance = EMPTY;
